import React, { useMemo, useState } from 'react';
import { Header } from '../../components/Header';
import {
  Box, Container, Typography, Paper, Chip, Button, CircularProgress, Stack,
  Tooltip, IconButton,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StarIcon from '@mui/icons-material/Star';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import InboxIcon from '@mui/icons-material/Inbox';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { myAllLoyaltyVouchers } from '../../apollo/server';
import { FONT_BODY } from '../../utils/designTokens';

const MY_ALL_VOUCHERS = gql`${myAllLoyaltyVouchers}`;

const FILTERS = [
  { key: 'ALL', label: 'All', icon: InboxIcon },
  { key: 'ACTIVE', label: 'Active', icon: CheckCircleOutlineIcon },
  { key: 'PAST', label: 'Past', icon: HistoryIcon },
];

function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  return Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
}

function expiryLabel(expiresAt) {
  const d = daysLeft(expiresAt);
  if (d == null) return null;
  if (d <= 0) return 'Expires today';
  if (d === 1) return 'Expires tomorrow';
  return `Expires in ${d} days`;
}

function expiryAccent(expiresAt) {
  const d = daysLeft(expiresAt);
  if (d == null) return '#16a34a';
  if (d <= 3) return '#dc2626';
  if (d <= 7) return '#d97706';
  return '#16a34a';
}

function VoucherTicket({ voucher }) {
  const [copied, setCopied] = useState(false);
  const isActive = voucher.status === 'ACTIVE';
  // Active coupons use a warm amber accent (matches the original design);
  // past ones desaturate to grey/red depending on terminal state.
  const accent = isActive
    ? '#d97706'
    : voucher.status === 'EXPIRED' ? '#dc2626' : '#64748b';
  const tint = isActive ? '#fef3c7' : voucher.status === 'EXPIRED' ? '#fee2e2' : '#f1f5f9';
  const border = isActive ? '#fcd34d' : voucher.status === 'EXPIRED' ? '#fca5a5' : '#cbd5e1';

  const copy = (e) => {
    e.stopPropagation();
    if (!voucher.voucherCode) return;
    navigator.clipboard?.writeText(voucher.voucherCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const expiry = expiryLabel(voucher.voucherExpiresAt);
  const expiryCol = expiryAccent(voucher.voucherExpiresAt);

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        display: 'flex',
        bgcolor: '#fff',
        borderRadius: '18px',
        border: `1px solid ${isActive ? border : '#e5e7eb'}`,
        overflow: 'hidden',
        transition: 'all 0.18s ease',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        opacity: isActive ? 1 : 0.78,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 26px rgba(15,23,42,0.10)',
          borderColor: accent,
        },
      }}
    >
      {/* Left stub */}
      <Box sx={{
        flexShrink: 0,
        width: { xs: 108, sm: 128 },
        bgcolor: tint,
        borderRight: `1.5px dashed ${accent}55`,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        p: 1.5,
        position: 'relative',
        '&::before, &::after': {
          content: '""', position: 'absolute', right: -8,
          width: 16, height: 16,
          bgcolor: '#f8fafc',
          borderRadius: '50%', border: '1px solid #e5e7eb',
        },
        '&::before': { top: -8 },
        '&::after': { bottom: -8 },
      }}>
        <LocalFireDepartmentIcon sx={{ color: accent, fontSize: 22, mb: 0.5 }} />
        <Typography sx={{
          color: accent, fontWeight: 900,
          fontSize: { xs: '1.05rem', sm: '1.2rem' },
          lineHeight: 1, letterSpacing: 0.2,
        }}>
          {voucher.voucherAmount ? `$${parseFloat(voucher.voucherAmount).toFixed(2)}` : 'REWARD'}
        </Typography>
        {voucher.voucherAmount && (
          <Typography sx={{
            color: accent, fontWeight: 700, fontSize: '0.65rem',
            mt: 0.25, letterSpacing: 0.5, textTransform: 'uppercase',
          }}>
            off
          </Typography>
        )}
      </Box>

      {/* Right content */}
      <Box sx={{ flex: 1, minWidth: 0, p: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: accent }}>
              <StorefrontIcon sx={{ fontSize: 14 }} />
              <Typography sx={{
                fontWeight: 800, fontSize: '0.72rem', letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}>
                {voucher.restaurantName || 'Restaurant Reward'}
              </Typography>
            </Stack>
            <Typography sx={{
              fontSize: '0.8rem', color: '#475569', fontWeight: 600, mt: 0.3,
              display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {voucher.promoName || 'Loyalty Reward'}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.75 }}>
              <Typography sx={{
                fontFamily: FONT_BODY, fontSize: '0.86rem', fontWeight: 700,
                color: '#0f172a', letterSpacing: 1.2,
              }}>
                {voucher.voucherCode}
              </Typography>
              {isActive && (
                <Tooltip title={copied ? 'Copied!' : 'Copy code'} placement="top">
                  <IconButton size="small" onClick={copy} sx={{ p: 0.25 }}>
                    {copied
                      ? <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#16a34a' }} />
                      : <ContentCopyIcon sx={{ fontSize: 14, color: '#94a3b8' }} />}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
          <Chip
            label={isActive ? 'Active' : (voucher.status === 'EXPIRED' ? 'Expired' : 'Used')}
            size="small"
            sx={{
              fontWeight: 700, fontSize: '0.7rem', height: 22,
              bgcolor: tint, color: accent, border: `1px solid ${border}`,
            }}
          />
        </Stack>

        {/* Footer: expiry / used date */}
        <Stack direction="row" alignItems="center" gap={0.4} sx={{ mt: 1.25 }}>
          {isActive && expiry ? (
            <>
              <AccessTimeIcon sx={{ fontSize: 12, color: expiryCol }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: expiryCol }}>
                {expiry}
                {voucher.voucherExpiresAt && ` · ${new Date(voucher.voucherExpiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
              </Typography>
            </>
          ) : voucher.usedAt ? (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8' }}>
                Used {new Date(voucher.usedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </>
          ) : voucher.status === 'EXPIRED' && voucher.voucherExpiresAt ? (
            <>
              <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8' }}>
                Expired {new Date(voucher.voucherExpiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </>
          ) : null}
        </Stack>

        {isActive && (
          <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ mt: 1.25, p: 1, borderRadius: '10px', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#b45309', mt: 0.15 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#92400e', lineHeight: 1.5 }}>
              Apply at checkout when ordering from <b>{voucher.restaurantName || 'this restaurant'}</b>.
            </Typography>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function PromotionalCoupons() {
  const { data, loading } = useQuery(MY_ALL_VOUCHERS, { fetchPolicy: 'network-only' });
  const vouchers = data?.myAllLoyaltyVouchers ?? [];

  const [filter, setFilter] = useState('ALL');

  const buckets = useMemo(() => {
    const active = [], past = [];
    for (const v of vouchers) (v.status === 'ACTIVE' ? active : past).push(v);
    active.sort((a, b) => new Date(a.voucherExpiresAt) - new Date(b.voucherExpiresAt));
    past.sort((a, b) => new Date(b.usedAt || b.voucherExpiresAt) - new Date(a.usedAt || a.voucherExpiresAt));
    return { active, past };
  }, [vouchers]);

  const totalSavings = useMemo(
    () => buckets.active.reduce((s, v) => s + (parseFloat(v.voucherAmount) || 0), 0),
    [buckets.active]
  );

  const counts = {
    ALL: vouchers.length,
    ACTIVE: buckets.active.length,
    PAST: buckets.past.length,
  };

  const dateStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Header />

      {/* Hero banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #FF3008 130%)',
        pt: { xs: 10, md: 12 },
        pb: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -120, right: -120,
          width: 380, height: 380, borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -160, left: -100,
          width: 320, height: 320, borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        },
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2.5}>
            <Box sx={{
              width: { xs: 52, md: 60 }, height: { xs: 52, md: 60 }, borderRadius: '14px',
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <LocalOfferIcon sx={{ color: '#fcd34d', fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.1rem' },
                color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em',
              }}>
                Promotional Coupons
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', mt: 0.4 }}>
                {dateStr} &middot; Restaurant spend-based rewards
              </Typography>
            </Box>
            {buckets.active.length > 0 && (
              <Box sx={{
                px: 2.5, py: 1.25, borderRadius: '14px',
                background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.18)',
                textAlign: 'center', minWidth: 140,
              }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Available savings
                </Typography>
                <Typography sx={{ color: '#fff', fontSize: '1.6rem', fontWeight: 800, lineHeight: 1 }}>
                  ${totalSavings.toFixed(2)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: -4, pb: 6, position: 'relative', zIndex: 2 }}>
        {/* Filter pills */}
        {!loading && vouchers.length > 0 && (
          <Box sx={{
            mb: 3, p: 1, bgcolor: '#fff', borderRadius: '16px',
            border: '1px solid #e5e7eb', display: 'inline-flex', gap: 0.5,
            boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
          }}>
            {FILTERS.map(f => {
              const Icon = f.icon;
              const active = filter === f.key;
              return (
                <Box
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    px: 2, py: 0.75, borderRadius: '12px', cursor: 'pointer',
                    bgcolor: active ? '#FF3008' : 'transparent',
                    color: active ? '#fff' : '#475569',
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: active ? '#FF3008' : '#f1f5f9' },
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    {f.label}
                  </Typography>
                  <Box sx={{
                    bgcolor: active ? 'rgba(255,255,255,0.22)' : '#e5e7eb',
                    color: active ? '#fff' : '#475569',
                    fontSize: '0.7rem', fontWeight: 800,
                    px: 0.75, py: 0.05, borderRadius: '8px',
                    minWidth: 22, textAlign: 'center',
                  }}>
                    {counts[f.key]}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#FF3008' }} />
          </Box>
        )}

        {!loading && vouchers.length === 0 && (
          <Box sx={{
            textAlign: 'center', py: 8, bgcolor: '#fff', borderRadius: '16px',
            border: '1px solid #e5e7eb',
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              bgcolor: '#fef3c7', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', mb: 2,
            }}>
              <LocalOfferIcon sx={{ fontSize: 40, color: '#d97706' }} />
            </Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#111827', mb: 0.5 }}>
              No promotional coupons yet
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#6b7280', mb: 2 }}>
              Order from participating restaurants to unlock spend-based rewards.
            </Typography>
            <Button
              component={Link}
              to="/restaurant-list"
              variant="contained"
              startIcon={<StorefrontIcon />}
              sx={{
                bgcolor: '#FF3008', color: '#fff', fontWeight: 700,
                textTransform: 'none', borderRadius: '10px', px: 3, py: 1,
                '&:hover': { bgcolor: '#e02b00' },
              }}
            >
              Browse restaurants
            </Button>
          </Box>
        )}

        {/* Active section */}
        {(filter === 'ALL' || filter === 'ACTIVE') && buckets.active.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '12px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(217,119,6,0.28)',
              }}>
                <LocalFireDepartmentIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827', lineHeight: 1.2 }}>
                  Active coupons
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>
                  Earned from spend at a restaurant — apply at that restaurant's checkout
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={2}>
              {buckets.active.map(v => <VoucherTicket key={v.voucherCode} voucher={v} />)}
            </Stack>
          </Box>
        )}

        {/* Past section */}
        {(filter === 'ALL' || filter === 'PAST') && buckets.past.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '12px',
                bgcolor: '#f1f5f9', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <HistoryIcon sx={{ color: '#64748b', fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827', lineHeight: 1.2 }}>
                  Past coupons
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>
                  Used or expired — for your records
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={2}>
              {buckets.past.map(v => <VoucherTicket key={v.voucherCode} voucher={v} />)}
            </Stack>
          </Box>
        )}

        {!loading && vouchers.length > 0 && (
          <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/my-coupons"
              variant="outlined"
              startIcon={<LocalOfferIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: '12px',
                px: 3, py: 1, borderColor: '#7c3aed', color: '#7c3aed',
                '&:hover': { borderColor: '#7c3aed', bgcolor: '#f3e8ff' },
              }}
            >
              My point coupons
            </Button>
            <Button
              component={Link}
              to="/rewards"
              variant="outlined"
              startIcon={<StarIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: '12px',
                px: 3, py: 1, borderColor: '#FF3008', color: '#FF3008',
                '&:hover': { borderColor: '#FF3008', bgcolor: '#fef2f2' },
              }}
            >
              My rewards
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default PromotionalCoupons;
